import React from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { ConversationItem } from "../../../utils/AIPrescription/types";
import { capitalizeFirstLetter } from "../../../utils/textUtils";
import UserIcon from "../UserIcon";
import AIResponseBox from "../AIResponseBox";
import {
  cardSx,
  cardContentSx,
  userQueryRowSx,
  userQueryBoxSx,
  userQueryTypographySx,
  motionDivStyle,
} from "./styles";

export interface ConversationCardProps {
  conversation: ConversationItem;
  isLatest: boolean;
  preferredPharmacy: string;
  primaryColor: string;
  onPharmacySelect: (conversationId: number, pharmacyName: string) => void;
  onFeedback: (conversationId: number, feedbackType: "up" | "down") => void;
  onSuggestionSelect: (suggestionText: string, medicationName?: string) => void;
  onSendPrescription: (message: string) => void;
  /** True while a previous turn is still in flight; disables Send/Send all on the
   *  summary card so a double-click can't submit the same prescription twice. */
  sendDisabled?: boolean;
  selectedMedicationPicks?: Record<string, string>;
  selectedPharmacyPicks?: Record<string, string>;
  onPharmacyLookupSelect: (
    pharmacyName: string,
    pharmacyId?: string,
    medicationName?: string
  ) => void;
  onPbmSelect: (displayMemberUniqueId: string, memberUniqueId?: string) => void;
  onFavouriteDrugSelect: (drug: any) => void;
  latestConversationRef: React.RefObject<HTMLDivElement | null>;
}

const ConversationCard: React.FC<ConversationCardProps> = ({
  conversation,
  isLatest,
  preferredPharmacy,
  primaryColor,
  onPharmacySelect,
  onFeedback,
  onSuggestionSelect,
  onSendPrescription,
  sendDisabled,
  selectedMedicationPicks,
  selectedPharmacyPicks,
  onPharmacyLookupSelect,
  onPbmSelect,
  onFavouriteDrugSelect,
  latestConversationRef,
}) => {
  return (
    <motion.div
      ref={isLatest ? (latestConversationRef as React.RefObject<HTMLDivElement>) : undefined}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      style={motionDivStyle}
    >
      <Card sx={cardSx}>
        <CardContent sx={cardContentSx}>
          {/* User Query */}
          <Box sx={userQueryRowSx}>
            <UserIcon color={primaryColor} />
            <Box sx={userQueryBoxSx}>
              <Typography variant="body1" component="div" sx={userQueryTypographySx}>
                {capitalizeFirstLetter(conversation.userQuery)}
              </Typography>
            </Box>
          </Box>
          {/* AI Response */}
          <AIResponseBox
            aiResponse={conversation.aiResponse}
            isLoading={conversation.isLoading}
            statusMessages={conversation.statusMessages}
            isLatest={isLatest}
            conversationId={conversation.id}
            showPharmacyList={conversation.showPharmacyList}
            prescriptions={conversation.prescriptions}
            preferredPharmacy={preferredPharmacy}
            primaryColor={primaryColor}
            feedback={conversation.feedback}
            onPharmacySelect={onPharmacySelect}
            onFeedback={onFeedback}
            suggestions={conversation.suggestions}
            suggestionGroups={conversation.suggestionGroups}
            onSuggestionSelect={onSuggestionSelect}
            onSendPrescription={onSendPrescription}
            sendDisabled={sendDisabled}
            selectedMedicationPicks={selectedMedicationPicks}
            pharmacyLookup={conversation.pharmacyLookup}
            pharmacySearchFallbackMode={conversation.pharmacySearchFallbackMode}
            pharmacySuggestionGroups={conversation.pharmacySuggestionGroups}
            selectedPharmacyPicks={selectedPharmacyPicks}
            onPharmacyLookupSelect={onPharmacyLookupSelect}
            pbmLookup={conversation.pbmLookup}
            onPbmSelect={onPbmSelect}
            favouriteDrugs={conversation.favouriteDrugs}
            onFavouriteDrugSelect={onFavouriteDrugSelect}
            drugInteractionResponse={conversation.drugInteractionResponse}
            allergyResponse={conversation.allergyResponse}
            currentState={conversation.currentState}
            mfaPhase={conversation.mfaPhase}
            structuredMedicationData={conversation.structuredMedicationData}
            intents={conversation.intents}
            primaryIntent={conversation.primaryIntent}
            uiBlocks={conversation.uiBlocks}
            appTabLink={conversation.appTabLink}
            appTabLinks={conversation.appTabLinks}
          />
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default ConversationCard;
